/**
 * schema.org JSON-LD builders. Everything is derived from data the page
 * already shows (organization details, FAQ entries, product pricings).
 */
import { isValidElement } from 'react'

import { LOGO_PATH, SITE_NAME, SITE_URL, absoluteUrl, organization } from './site'

const ORGANIZATION_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

/** Plain text of a React node (strings, numbers, nested elements / fragments). */
export function nodeToText(node) {
    if (node === null || node === undefined || typeof node === 'boolean') return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) return node.map(nodeToText).join('')
    if (isValidElement(node)) {
        const text = nodeToText(node.props.children)
        // Block-level elements read as separate sentences.
        return ['p', 'div', 'li', 'br'].includes(node.type) ? ` ${text} ` : text
    }
    return ''
}

const cleanText = (text) => text.replace(/\s+/g, ' ').trim()

export function organizationSchema() {
    const { address, openingHours } = organization
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: organization.name,
        legalName: organization.legalName,
        url: absoluteUrl('/'),
        logo: absoluteUrl(LOGO_PATH),
        email: organization.email,
        telephone: organization.telephone,
        address: { '@type': 'PostalAddress', ...address },
        contactPoint: [
            {
                '@type': 'ContactPoint',
                contactType: 'sales',
                telephone: organization.telephone,
                email: organization.email,
                areaServed: 'ID',
                hoursAvailable: {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: openingHours.days,
                    opens: openingHours.opens,
                    closes: openingHours.closes,
                },
            },
            {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                telephone: organization.officeTelephone,
                areaServed: 'ID',
            },
        ],
        sameAs: organization.sameAs,
        parentOrganization: {
            '@type': 'Organization',
            name: organization.parentOrganization.name,
            url: organization.parentOrganization.url,
        },
    }
}

export function websiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: SITE_NAME,
        url: absoluteUrl('/'),
        inLanguage: 'en',
        publisher: { '@id': ORGANIZATION_ID },
    }
}

/** items: [{ name, path }], first is usually Home. */
export function breadcrumbSchema(items) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.name,
            item: absoluteUrl(item.path),
        })),
    }
}

export function faqSchema(entries) {
    const mainEntity = entries
        .map((entry) => ({ question: cleanText(nodeToText(entry.question)), answer: cleanText(nodeToText(entry.answer)) }))
        .filter((entry) => entry.question && entry.answer)
        .map((entry) => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: { '@type': 'Answer', text: entry.answer },
        }))
    if (mainEntity.length === 0) return null
    return { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity }
}

const isListedProduct = (product) => Boolean(product) && !product.beta && !product.comingSoon

const isPrice = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0

/** Offers built from product.pricings; plans without a monthly price are skipped. */
function offersFor(product) {
    const plans = Array.isArray(product.pricings) ? product.pricings : []
    return plans
        .filter((plan) => plan && plan.name && isPrice(plan.monthlyPrice))
        .map((plan) => {
            const priceSpecification = [
                {
                    '@type': 'UnitPriceSpecification',
                    name: 'Monthly subscription',
                    price: plan.monthlyPrice,
                    priceCurrency: 'IDR',
                    billingDuration: 'P1M',
                    unitCode: 'MON',
                    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
                },
            ]
            if (isPrice(plan.annualPrice)) {
                priceSpecification.push({
                    '@type': 'UnitPriceSpecification',
                    name: 'Annual subscription',
                    price: plan.annualPrice,
                    priceCurrency: 'IDR',
                    billingDuration: 'P1Y',
                    unitCode: 'ANN',
                    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'ANN' },
                })
            }
            if (isPrice(plan.corePrice)) {
                priceSpecification.push({
                    '@type': 'PriceSpecification',
                    name: 'Core pricing',
                    price: plan.corePrice,
                    priceCurrency: 'IDR',
                })
            }
            return {
                '@type': 'Offer',
                name: `${product.name} ${plan.name}`,
                url: absoluteUrl('/pricing'),
                price: plan.monthlyPrice,
                priceCurrency: 'IDR',
                availability: 'https://schema.org/InStock',
                priceSpecification,
                seller: { '@id': ORGANIZATION_ID },
            }
        })
}

/** SoftwareApplication with offers, or null for beta / coming-soon / unpriced products. */
export function softwareApplicationSchema(product, description) {
    if (!isListedProduct(product)) return null
    const offers = offersFor(product)
    if (offers.length === 0) return null
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: product.name,
        description: description || product.description,
        url: absoluteUrl(`/product/${product.slug}`),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        provider: { '@id': ORGANIZATION_ID },
        offers,
    }
}
